import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Row, Col, Modal } from 'react-bootstrap';
import { resetPassword, updateProfileDetails } from 'src/redux/Actions/Auth';
import UserProfile from 'src/Images/Profile.png';
import { LargeAvatarIcon } from 'src/components/Layout/NavbarStyles';
import 'src/style/ProfileGeneral.css';
import { getProfileDetails } from 'src/redux/Actions/Settings';
import { ReactComponent as Eye } from 'src/Images/icons/common/eye.svg';
import { ReactComponent as EyeOff } from 'src/Images/icons/common/EyeOff.svg';
import { ReactComponent as Close } from 'src/Images/icons/common/close.svg';
import getColorCode from 'src/utils/GetThemeColor';
import { Alert } from '@mui/material';
import usePageTitle from 'src/utils/SetPageTitle';
import { useSetBreadcrumbData } from 'src/utils/SetBreadcrumbData';
import Breadcrumb from 'src/components/Breadcrumb';
import { useLocation } from 'react-router-dom';
import { ReactComponent as ProfileGeneralCover } from 'src/Images/icons/common/ProfileGeneralCover.svg';
import { ReactComponent as Edit } from 'src/Images/icons/common/edit.svg';
import Slider from '@mui/material/Slider';
import { useFontSize } from 'src/components/FontSizeContext';
import { ReactComponent as DropdownIcon } from 'src/Images/icons/common/dropdown.svg';
import { ReactComponent as SmallFontIcon } from 'src/Images/icons/common/smallFontSizeIcon.svg';
import { ReactComponent as DefaultFontIcon } from 'src/Images/icons/common/defaultFontSizeIcon.svg';
import { ReactComponent as LargeFontIcon } from 'src/Images/icons/common/largeFontSizeIcon.svg';
import { ReactComponent as ExtraLargeFontIcon } from 'src/Images/icons/common/extraLargeFontSizeIcon.svg';
import CustomMultiSelect from 'src/utils/CustomMultiSelect';

const Cropper = React.forwardRef(({ image, zoom, cropShape }, ref) => {
  const canvasRef = React.useRef(null);
  const imgRef = React.useRef(null);
  const [offset, setOffset] = React.useState({ x: 0, y: 0 });
  const [dragging, setDragging] = React.useState(false);
  const start = React.useRef({ x: 0, y: 0 });

  React.useImperativeHandle(ref, () => ({
    getCanvas: () => canvasRef.current,
  }));

  React.useEffect(() => {
    const img = new Image();
    img.src = image;
    img.onload = () => {
      imgRef.current = img;
      setOffset({ x: 0, y: 0 });
      draw();
    };
  }, [image, zoom]);

  const draw = () => {
    const canvas = canvasRef.current;
    if (!canvas || !imgRef.current) return;

    const ctx = canvas.getContext('2d');
    const size = 300;

    canvas.width = size;
    canvas.height = size;

    ctx.clearRect(0, 0, size, size);

    const img = imgRef.current;
    const scale = Math.max(size / img.width, size / img.height) * zoom;

    const scaledWidth = img.width * scale;
    const scaledHeight = img.height * scale;

    const x = (size - scaledWidth) / 2 + offset.x;
    const y = (size - scaledHeight) / 2 + offset.y;

    ctx.drawImage(img, x, y, scaledWidth, scaledHeight);
  };

  return (
    <canvas
      ref={canvasRef}
      style={{
        width: '300px',
        height: '300px',
        borderRadius: cropShape === 'round' ? '50%' : '0%',
        cursor: 'grab',
      }}
      onMouseDown={(e) => {
        setDragging(true);
        start.current = { x: e.clientX, y: e.clientY };
      }}
      onMouseMove={(e) => {
        if (!dragging) return;

        const dx = e.clientX - start.current.x;
        const dy = e.clientY - start.current.y;

        setOffset((prev) => ({
          x: prev.x + dx,
          y: prev.y + dy,
        }));

        start.current = { x: e.clientX, y: e.clientY };
        draw();
      }}
      onMouseUp={() => setDragging(false)}
      onMouseLeave={() => setDragging(false)}
    />
  );
});

export default function General() {
  usePageTitle('Cloud CRM • Profile');
  const dispatch = useDispatch();
  const location = useLocation();
  const authReducer = useSelector((state) => state.AuthReducer);
  const profileImage = useSelector(
    (state) => state.SettingsReducer.profileImage,
  );
  const themeColor = authReducer?.userData?.tenant?.theme;
  const tenantId = authReducer?.userData?.tenant?.id;
  const currentProfileId = authReducer?.userData?.profile_id;
  const [profileId, setProfileId] = useState(null);
  const showResetPassword =
    profileId && String(profileId) === String(currentProfileId);
  const [showResetModal, setShowResetModal] = useState(false);

  const cropperRef = React.useRef(null);

  const [formData, setFormData] = useState({
    currentPassword: null,
    newPassword: null,
    confirmPassword: null,
  });

  const [passwordVisibility, setPasswordVisibility] = useState({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false,
  });

  const handleProfileChange = (e) => {
    const { name, value } = e.target;

    setProfileFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const [responseSuccessMessage, setResponseSuccessMessage] = useState(null);

  const [responseErrorMessage, setResponseErrorMessage] = useState(null);

  const [profileFormData, setProfileFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    mobileNumber: '',
    profilePictureFile: null,
    previewImage: null,
  });
  const [isFormEditingEnabled, setIsFormEditingEnabled] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [profileDetails, setProfileDetails] = useState({});
  const userTypeId = profileDetails?.userType?.id;

  const [cropState, setCropState] = useState({
    isOpen: false,
    imageSrc: null,
    crop: { x: 0, y: 0 },
    zoom: 1,
    croppedAreaPixels: null,
  });

  const sanitizeFileNamePart = (value = '') =>
    String(value)
      .trim()
      .replace(/\s+/g, '_')
      .replace(/[^a-zA-Z0-9_-]/g, '');

  const getProfileImageFileName = () => {
    const firstNamePart =
      sanitizeFileNamePart(profileFormData.firstName) || 'first_name';
    const lastNamePart =
      sanitizeFileNamePart(profileFormData.lastName) || 'last_name';
    const profileIdPart =
      sanitizeFileNamePart(profileId || profileDetails?.id) || 'profile_id';

    return `${firstNamePart}_${lastNamePart}_${profileIdPart}.jpg`;
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const MAX_PROFILE_FILE_SIZE_BYTES = 1024 * 1024; // 1 MB
    if (file.size > MAX_PROFILE_FILE_SIZE_BYTES) {
      setResponseErrorMessage(
        'Error uploading profile image. File size must be 1 MB or less.',
      );
      e.target.value = '';
      return;
    }

    setResponseErrorMessage(null);
    setIsFormEditingEnabled(true);

    const preview = URL.createObjectURL(file);

    // open crop modal
    setCropState((prev) => ({
      ...prev,
      isOpen: true,
      imageSrc: preview,
    }));
  };

  const handleCropChoose = () => {
    const canvas = cropperRef.current?.getCanvas();
    if (!canvas) return;

    canvas.toBlob((blob) => {
      const fileName = getProfileImageFileName();
      const file = new File([blob], fileName, { type: 'image/jpeg' });
      const previewUrl = URL.createObjectURL(blob);

      setProfileFormData((prev) => ({
        ...prev,
        profilePictureFile: file,
        previewImage: previewUrl,
      }));

      setCropState({
        isOpen: false,
        imageSrc: null,
        crop: { x: 0, y: 0 },
        zoom: 1,
        croppedAreaPixels: null,
      });
    });
  };

  const onCropComplete = useCallback((_, croppedPixels) => {
    setCropState((prev) => ({
      ...prev,
      croppedAreaPixels: croppedPixels,
    }));
  }, []);

  const handleCropCancel = () => {
    setCropState({
      isOpen: false,
      imageSrc: null,
      crop: { x: 0, y: 0 },
      zoom: 1,
      croppedAreaPixels: null,
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const togglePasswordVisibility = (field) => {
    setPasswordVisibility((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const { resetPasswordResponse } = authReducer;

  const handleSubmit = (e) => {
    e.preventDefault();

    setResponseErrorMessage(null);

    const resetPasswordPayload = {
      email: profileDetails?.email,
      current_password: formData.currentPassword,
      new_password: formData.newPassword,
      confirm_password: formData.confirmPassword,
    };

    dispatch(resetPassword(resetPasswordPayload));
  };

  useEffect(() => {
    if (
      (resetPasswordResponse && resetPasswordResponse.error) ||
      resetPasswordResponse.success
    ) {
      const { error, success } = resetPasswordResponse;
      if (error) {
        setResponseErrorMessage(error);
      } else {
        setFormData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: '',
        });
        setResponseErrorMessage(null);
        if (success) {
          setResponseSuccessMessage(success);
        }
      }
    }
  }, [resetPasswordResponse]);

  useEffect(() => {
    const queryProfileId = new URLSearchParams(location.search).get(
      'profile_id',
    );
    setProfileId(queryProfileId || currentProfileId || null);
  }, [currentProfileId, location.search]);

  useEffect(() => {
    if (!tenantId || !profileId) return;

    dispatch(getProfileDetails(tenantId, profileId))
      .then((data) => {
        setProfileDetails(data || {});
        setProfileFormData((prev) => ({
          ...prev,
          firstName: data?.firstName || '',
          lastName: data?.lastName || '',
          email: data?.email || '',
          mobileNumber: data?.mobileNumber || '',
          profilePictureFile: null,
          previewImage: null,
        }));
      })
      .finally(() => {
        setIsFormEditingEnabled(false);
      });
  }, [dispatch, profileId, tenantId]);

  const handleUpdateSubmit = () => {
    setIsSubmitting(true);

    const profileFD = new FormData();
    profileFD.append('profile_id', profileId || '');
    profileFD.append('tenant_id', tenantId);
    profileFD.append('first_name', profileFormData.firstName);
    profileFD.append('last_name', profileFormData.lastName);
    profileFD.append('email', profileFormData.email);
    profileFD.append('mobile_number', profileFormData.mobileNumber);

    // add image only if user selected one
    if (profileFormData.profilePictureFile) {
      profileFD.append('profile_picture', profileFormData.profilePictureFile);
    }

    dispatch(updateProfileDetails(profileFD))
      .then(() => dispatch(getProfileDetails(tenantId, profileId)))
      .then((data) => {
        setProfileDetails(data || {});
        setProfileFormData((prev) => ({
          ...prev,
          firstName: data?.firstName || '',
          lastName: data?.lastName || '',
          email: data?.email || '',
          mobileNumber: data?.mobileNumber || '',
          profilePictureFile: null,
          previewImage: null,
        }));
        setIsFormEditingEnabled(false);
        setResponseSuccessMessage('Profile updated successfully!');
      })
      .finally(() => setIsSubmitting(false));
  };

  useSetBreadcrumbData(location.pathname, 'Profile');
  const canEditName = userTypeId === 3;
  const canEditContact = userTypeId === 2;

  const { currentFontSize, setCurrentFontSize, fontSuggestion } = useFontSize();

  const fontSizeOptions = [
    {
      value: 'xsmall',
      label: (
        <div className="div-flex-row align-items-center div-space-between w-100">
          <span className="text-xsm">Extra Small</span>
          <SmallFontIcon className="icon-size-xsm" />
        </div>
      ),
    },
    {
      value: 'small',
      label: (
        <div className="div-flex-row align-items-center div-space-between w-100">
          <span className="text-sm">Small</span>
          <SmallFontIcon className="icon-size-sm" />
        </div>
      ),
    },
    {
      value: 'default',
      label: (
        <div className="div-flex-row align-items-center div-space-between w-100">
          <span className="text-md">Default</span>
          <DefaultFontIcon className="icon-size-md" />
        </div>
      ),
    },
    {
      value: 'large',
      label: (
        <div className="div-flex-row align-items-center div-space-between w-100">
          <span className="text-lg">Large</span>
          <LargeFontIcon className="icon-size-lg" />
        </div>
      ),
    },
    {
      value: 'xlarge',
      label: (
        <div className="div-flex-row align-items-center div-space-between w-100">
          <span className="text-xl">Extra Large</span>
          <ExtraLargeFontIcon className="icon-size-xl" />
        </div>
      ),
    },
  ];

  const formatFontOptionLabel = (option, { context }) => {
    if (context === 'menu') {
      return option.label;
    }
    const [textElement] = option.label?.props?.children || [];
    return <span className="text-sm">{textElement?.props?.children}</span>;
  };

  const fontSizeLabels = {
    xsmall: 'Extra Small',
    small: 'Small',
    default: 'Default',
    large: 'Large',
    xlarge: 'Extra Large',
  };

  return (
    <div className="div-flex-column">
      <div className="page-layout div-flex-column">
        <Row className="g-0">
          <Col xxl={12} xl={12} lg={12} md={12}>
            <div className="div-space-between slider-padding left-padding quotation-list-container">
              <div className="div-flex-row cg-5">
                <Breadcrumb />
              </div>
            </div>
          </Col>
        </Row>

        <div>
          <div>
            <div className="general-profile-header-template">
              <ProfileGeneralCover color={getColorCode(themeColor)} />
            </div>
            <div className="div-flex-row cg-20">
              <div className="avatar-container general-profile-avatar">
                <LargeAvatarIcon
                  src={
                    profileFormData.previewImage || profileImage || UserProfile
                  }
                  alt="photoURL"
                  id="accountIcon"
                />
                <div className="camera-button avatar-container icon-size-xl div-align-center">
                  <label
                    className="cursor-pointer"
                    htmlFor="profile-picture-input"
                    aria-label="Upload profile picture"
                  >
                    <Edit className="icon-size-xsm" color="#FFFFFF" />
                    <input
                      id="profile-picture-input"
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      hidden
                    />
                  </label>
                </div>
              </div>
              <div className="left-padding">
                <div className="text-xxl text-bold py-1">
                  {profileDetails?.name}
                </div>
                <div className="text-sm">
                  {profileDetails?.mobileNumber} | {profileDetails?.email}
                </div>
              </div>
            </div>
          </div>

          <Row className="m-0 general-profile-body-details">
            <Col xxl={12} xl={12} lg={12} md={12} sm={12} xs={12}>
              <div className="profile-header-row">
                <div className="text-lg text-semi-bold">
                  Personal Information
                </div>

                <div className="right-actions">
                  {(profileDetails?.userType?.id === 2 ||
                    profileDetails?.userType?.id === 1) && (
                    <div className="role-badge text-md border-radius-4 text-semi-bold">
                      {profileDetails?.userType?.name}
                    </div>
                  )}

                  {isFormEditingEnabled ? (
                    <button
                      className={`common-btn text-sm theme-background-${themeColor}`}
                      disabled={isSubmitting}
                      onClick={handleUpdateSubmit}
                    >
                      {isSubmitting ? 'Saving...' : 'Save'}
                    </button>
                  ) : (
                    <div
                      role="button"
                      tabIndex={0}
                      aria-label="Edit personal information"
                      className={`div-align-center create-icon-btn padding-top-8 padding-bottom-8 theme-background-${themeColor}`}
                      onClick={() => setIsFormEditingEnabled(true)}
                    >
                      <Edit className="icon-size-md" color="#FFFFFF" />
                    </div>
                  )}
                </div>
              </div>
            </Col>

            <Row className="m-0 p-0 rg-5">
              <Col
                xxl={6}
                xl={6}
                lg={6}
                md={6}
                sm={12}
                xs={12}
                className="form-input-container"
              >
                <div className="div-flex-column">
                  <div className="text-sm text-semi-bold profile-info-lable">
                    First Name
                  </div>
                  <div className="input-wrapper">
                    <input
                      className="form-input-section input-icon-remove"
                      name="firstName"
                      value={profileFormData.firstName}
                      disabled={!isFormEditingEnabled || !canEditName}
                      onChange={handleProfileChange}
                    />
                  </div>
                </div>
              </Col>

              <Col
                xxl={6}
                xl={6}
                lg={6}
                md={6}
                sm={12}
                xs={12}
                className="form-input-container"
              >
                <div className="div-flex-column">
                  <div className="text-sm text-semi-bold profile-info-lable">
                    Last Name
                  </div>
                  <div className="input-wrapper">
                    <input
                      className="form-input-section input-icon-remove"
                      name="lastName"
                      value={profileFormData.lastName}
                      disabled={!isFormEditingEnabled || !canEditName}
                      onChange={handleProfileChange}
                    />
                  </div>
                </div>
              </Col>

              <Col
                xxl={6}
                xl={6}
                lg={6}
                md={6}
                sm={12}
                xs={12}
                className="form-input-container"
              >
                <div className="div-flex-column">
                  <div className="text-sm text-semi-bold profile-info-lable">
                    Email
                  </div>
                  <div className="input-wrapper">
                    <input
                      className="form-input-section input-icon-remove"
                      name="email"
                      value={profileFormData.email}
                      disabled={!isFormEditingEnabled || !canEditContact}
                      onChange={handleProfileChange}
                    />
                  </div>
                </div>
              </Col>

              <Col
                xxl={6}
                xl={6}
                lg={6}
                md={6}
                sm={12}
                xs={12}
                className="form-input-container"
              >
                <div className="div-flex-column">
                  <div className="text-sm text-semi-bold profile-info-lable">
                    Mobile Number
                  </div>
                  <div className="input-wrapper">
                    <input
                      className="form-input-section input-icon-remove"
                      name="mobileNumber"
                      type="number"
                      value={profileFormData.mobileNumber}
                      disabled={!isFormEditingEnabled || !canEditContact}
                      onChange={handleProfileChange}
                    />
                  </div>
                </div>
              </Col>

              <Col
                xxl={6}
                xl={6}
                lg={6}
                md={6}
                sm={12}
                xs={12}
                className="form-input-container"
              >
                <div className="div-flex-column">
                  <div className="text-sm text-semi-bold profile-info-lable">
                    User Role
                  </div>
                  <input
                    className="form-input-section"
                    value={profileDetails?.userRole?.name || ''}
                    disabled
                  />
                </div>
              </Col>
              <Col
                xxl={6}
                xl={6}
                lg={6}
                md={6}
                sm={12}
                xs={12}
                className="form-input-container"
              >
                <div className="div-flex-column">
                  <div className="text-sm text-semi-bold profile-info-lable">
                    Position
                  </div>
                  <input
                    className="form-input-section"
                    value={profileDetails?.position?.name || ''}
                    disabled
                  />
                </div>
              </Col>
              <Col
                xxl={6}
                xl={6}
                lg={6}
                md={6}
                sm={12}
                xs={12}
                className="form-input-container"
              >
                <div className="div-flex-column">
                  <div className="text-sm text-semi-bold profile-info-lable">
                    Department
                  </div>
                  <input
                    className="form-input-section"
                    value={profileDetails?.department?.name || ''}
                    disabled
                  />
                </div>
              </Col>
            </Row>
          </Row>
          <Row className="m-0 general-profile-body">
            {showResetPassword && (
              <Col xxl={12} xl={12} lg={12} md={12} sm={12} xs={12}>
                <div className="div-flex-column rg-10">
                  <div className="text-lg text-semi-bold border-top padding-top-16">
                    Password
                  </div>
                  <div className="text-grey text-sm">
                    Set a password that is unique
                  </div>
                  <div className="padding-bottom-8">
                    <button
                      type="submit"
                      onClick={() => setShowResetModal(true)}
                      className={`common-btn theme-background-${themeColor}`}
                    >
                      <div className="text-sm">Reset Password</div>
                    </button>
                  </div>
                  {/* Reset Password Modal */}
                  <Modal
                    show={showResetModal}
                    onHide={() => setShowResetModal(false)}
                    centered
                    size="md"
                  >
                    <div className="div-flex-column p-0">
                      <div className="reset-password-modal-header div-flex-row div-space-between w-100">
                        <div className="div-flex-column">
                          <div className="text-md text-semi-bold">
                            Reset your password
                          </div>
                          <div className="text-xs text-grey">
                            New password must be different from previous
                            passwords.
                          </div>
                        </div>
                        <div>
                          <button
                            type="button"
                            className="icon-btn-none"
                            aria-label="Close"
                            onClick={() => setShowResetModal(false)}
                          >
                            <Close
                              className="icon-size-sm"
                              color={getColorCode(themeColor)}
                            />
                          </button>
                        </div>
                      </div>
                    </div>
                    <Modal.Body>
                      <Row className="m-0">
                        <Col
                          xxl={12}
                          xl={12}
                          lg={12}
                          md={12}
                          sm={12}
                          xs={12}
                          className="form-input-container"
                        >
                          <div className="div-flex-column">
                            <div className="text-sm text-semi-bold profile-info-lable">
                              Email
                            </div>
                            <input
                              type="email"
                              name="email"
                              className="form-input-section"
                              value={profileDetails?.email || ''}
                              required
                              disabled
                            />
                          </div>
                        </Col>
                        <Col
                          xxl={12}
                          xl={12}
                          lg={12}
                          md={12}
                          sm={12}
                          xs={12}
                          className="form-input-container"
                        >
                          <div className="div-flex-column">
                            <div className="text-sm text-semi-bold profile-info-lable">
                              Current Password
                            </div>
                            <div className="password-input-container">
                              <input
                                type={
                                  passwordVisibility.currentPassword
                                    ? 'text'
                                    : 'password'
                                }
                                name="currentPassword"
                                className="form-input-section"
                                value={formData.currentPassword}
                                onChange={handleChange}
                                placeholder="Enter current password"
                                required
                              />
                              <button
                                type="button"
                                className="eye-icon-btn"
                                aria-label={
                                  passwordVisibility.currentPassword
                                    ? 'Hide password'
                                    : 'Show password'
                                }
                                onClick={() =>
                                  togglePasswordVisibility('currentPassword')
                                }
                              >
                                {passwordVisibility.currentPassword ? (
                                  <EyeOff className="icon-size-lg" />
                                ) : (
                                  <Eye className="icon-size-lg" />
                                )}
                              </button>
                            </div>
                          </div>
                        </Col>
                        <Col
                          xxl={12}
                          xl={12}
                          lg={12}
                          md={12}
                          sm={12}
                          xs={12}
                          className="form-input-container"
                        >
                          <div className="div-flex-column">
                            <div className="text-sm text-semi-bold profile-info-lable">
                              New Password
                            </div>
                            <div className="password-input-container">
                              <input
                                type={
                                  passwordVisibility.newPassword
                                    ? 'text'
                                    : 'password'
                                }
                                name="newPassword"
                                className="form-input-section"
                                value={formData.newPassword}
                                onChange={handleChange}
                                placeholder="Enter new password"
                                required
                              />
                              <button
                                type="button"
                                className="eye-icon-btn"
                                aria-label={
                                  passwordVisibility.newPassword
                                    ? 'Hide password'
                                    : 'Show password'
                                }
                                onClick={() =>
                                  togglePasswordVisibility('newPassword')
                                }
                              >
                                {passwordVisibility.newPassword ? (
                                  <EyeOff className="icon-size-lg" />
                                ) : (
                                  <Eye className="icon-size-lg" />
                                )}
                              </button>
                            </div>
                          </div>
                        </Col>
                        <Col
                          xxl={12}
                          xl={12}
                          lg={12}
                          md={12}
                          sm={12}
                          xs={12}
                          className="form-input-container"
                        >
                          <div className="div-flex-column">
                            <div className="text-sm text-semi-bold profile-info-lable">
                              Confirm Password
                            </div>
                            <div className="password-input-container">
                              <input
                                type={
                                  passwordVisibility.confirmPassword
                                    ? 'text'
                                    : 'password'
                                }
                                name="confirmPassword"
                                className="form-input-section"
                                value={formData.confirmPassword}
                                onChange={handleChange}
                                placeholder="Confirm password"
                                required
                              />
                              <button
                                type="button"
                                className="eye-icon-btn"
                                aria-label={
                                  passwordVisibility.confirmPassword
                                    ? 'Hide password'
                                    : 'Show password'
                                }
                                onClick={() =>
                                  togglePasswordVisibility('confirmPassword')
                                }
                              >
                                {passwordVisibility.confirmPassword ? (
                                  <EyeOff className="icon-size-lg" />
                                ) : (
                                  <Eye className="icon-size-lg" />
                                )}
                              </button>
                            </div>
                          </div>
                        </Col>
                      </Row>
                      <div className="padding-top-16 padding-bottom-8 div-align-center justify-content-center">
                        <button
                          type="submit"
                          onClick={handleSubmit}
                          className={`common-btn theme-background-${themeColor}`}
                        >
                          <div className="text-sm">Change Password</div>
                        </button>
                      </div>
                    </Modal.Body>
                  </Modal>
                </div>
              </Col>
            )}
          </Row>
          <Row className="m-0 font-visibility-body">
            <div className="border-top padding-bottom-8">
              <Col xxl={2} xl={2} lg={2} className="form-input-container p-0">
                <div className="div-flex-column rg-10">
                  <div className="text-lg text-semi-bold padding-top-16">
                    Font Visibility
                  </div>

                  <div className="input-wrapper">
                    <CustomMultiSelect
                      isMulti={false}
                      name="fontSize"
                      options={fontSizeOptions}
                      className="multi-select-input-section"
                      classNamePrefix="react-select"
                      value={fontSizeOptions.find(
                        (opt) => opt.value === currentFontSize,
                      )}
                      onChange={(selectedOption) =>
                        setCurrentFontSize(selectedOption.value)
                      }
                      placeholder="Select font size"
                      formatOptionLabel={formatFontOptionLabel}
                    />

                    <DropdownIcon
                      className="icon-size-sm form-input-section-icon"
                      color={getColorCode(themeColor)}
                    />
                  </div>

                  {fontSuggestion && fontSuggestion !== currentFontSize && (
                    <div className="text-xs text-muted padding-top-4">
                      Based on your screen size, the recommended font setting is{' '}
                      <span className="text-sm text-semi-bold">
                        {fontSizeLabels[fontSuggestion]}.
                      </span>
                    </div>
                  )}
                </div>
              </Col>
            </div>
          </Row>
        </div>
      </div>
      <Modal
        show={cropState.isOpen}
        onHide={handleCropCancel}
        centered
        size="md"
      >
        <div className="p-3">
          <div className="text-md text-semi-bold mb-2">Adjust your photo</div>
          <div className="cropper-container">
            <Cropper
              ref={cropperRef}
              image={cropState.imageSrc}
              crop={cropState.crop}
              zoom={cropState.zoom}
              aspect={1}
              cropShape="round"
              onCropChange={(crop) =>
                setCropState((prev) => ({ ...prev, crop }))
              }
              onZoomChange={(zoom) =>
                setCropState((prev) => ({ ...prev, zoom }))
              }
              onCropComplete={onCropComplete}
            />
          </div>

          <div className="mt-3">
            <Slider
              min={1}
              max={3}
              step={0.1}
              value={cropState.zoom}
              onChange={(e, newZoom) =>
                setCropState((prev) => ({
                  ...prev,
                  zoom: newZoom,
                }))
              }
            />
          </div>

          <div className="div-flex-row div-space-between mt-3">
            <button
              className="common-btn bg-gray-300 text-dark"
              onClick={handleCropCancel}
            >
              Cancel
            </button>
            <button
              className={`common-btn theme-background-${themeColor}`}
              onClick={handleCropChoose}
            >
              Choose
            </button>
          </div>
        </div>
      </Modal>
      <Modal
        show={responseSuccessMessage}
        onHide={() => setResponseSuccessMessage(null)}
      >
        <Alert severity="success">{responseSuccessMessage}</Alert>
      </Modal>
      <Modal
        show={responseErrorMessage}
        onHide={() => setResponseErrorMessage(null)}
      >
        <Alert severity="error">{responseErrorMessage}</Alert>
      </Modal>
    </div>
  );
}
